import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { Fragment, useState, useEffect } from 'react';
import { Button } from '~/components/common/button';
import { SubmitLoader } from '~/components/common/loaders/submit';
import { Translate, useTranslate } from '~/i18n';
import { ArrowDown, TickMark } from '~/svgs';
import { BookingProps, TicketProps } from '~/interfaces/booking';
import Axios from '~/utils/axios';
import { errorMessage } from '~/utils/toasts';
import { sentryCaptureException } from '~/errors/sentry';

const AttendeeTable = ({ loading, data }: any) => {
   return (
      <div className="min-h-screen overflow-auto">
         {loading ? (
            <div className="flex justify-center h-full items-center">
               <SubmitLoader />
            </div>
         ) : (
            <div className="grid grid-cols-5">
               <div className="col-span-5">
                  <div>
                     <table className="w-full xl:text-sm text-xs table-auto">
                        <thead className="uppercase ltr:text-left rtl:text-right whitespace-nowrap">
                           <tr>
                              <th className="text-gray-400 bg-gray-100 h-11 p-2 rtl:rounded-r-md ltr:rounded-l-md">
                                 <Translate id="operations:attendee info" />
                              </th>
                              <th className="text-gray-400 bg-gray-100 h-11 p-2">
                                 <Translate id="common:product" />
                              </th>
                              <th className="text-gray-400 bg-gray-100 h-11 p-2">
                                 <Translate id="bookings:quantity" />
                              </th>
                              <th className="text-gray-400 bg-gray-100 h-11 p-2">
                                 <Translate id="payout:date" />
                              </th>

                              <th className="text-gray-400 bg-gray-100 h-11 p-2 rtl:rounded-l-md ltr:rounded-r-md"></th>
                              <th className="text-gray-400 bg-gray-100 h-11 p-2 rtl:rounded-l-md ltr:rounded-r-md"></th>
                           </tr>
                        </thead>
                        <tbody className="ltr:text-left rtl:text-right whitespace-nowrap text-black">
                           {data?.data?.map((booking: BookingProps, index: number) => (
                              <Fragment key={index}>
                                 <AttendeeItem booking={booking} />
                              </Fragment>
                           ))}
                           {!data?.data.length && (
                              <tr>
                                 <td colSpan={6}>
                                    <div className="text-center w-full xl:text-sm text-xs py-8 border-b border-gray-200 border-dashed">
                                       <Translate id="operations:no attendees" />
                                    </div>
                                 </td>
                              </tr>
                           )}
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

type AttendeeItemType = {
   booking: BookingProps;
};

const AttendeeItem = ({ booking }: AttendeeItemType) => {
   const [open, setOpen] = useState(false);
   const [checkAll, setCheckAll] = useState(
      booking?.tackets.every(ticket => ticket.checked_in === true)
   );

   const getCheckAll = async () => {
      try {
         const { data } = await Axios.post(`/hy-providers/v1/bookings/${booking?._id}/check-in`, {
            check_all: true,
         });
         if (data?.status === 'Success') {
            setCheckAll(true);
         } else {
            throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error: any) {
         sentryCaptureException(error, {
            tags: { journey: 'business' },
         });
         errorMessage(error?.response?.data?.message);
         console.log(error);
      }
   };

   return (
      <>
         <tr className="border-b border-gray-200 xl:text-base text-sx border-dashed">
            <td className="py-7 p-2">
               <div className="capitalize">{booking?.customer.name}</div>
               <div className="text-gray-400">{booking?.price}</div>
            </td>

            <td className="p-2">
               <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <Image
                     src={booking?.product?.cover_image || '/images/placeholder-image.svg'}
                     width={65}
                     height={48}
                     className="rounded-md"
                     layout="fixed"
                  />

                  <div>{booking?.product?.name}</div>
               </div>
            </td>
            <td className="p-2">{booking?.tackets_count}</td>
            <td className="p-2">{booking?.date_issued}</td>
            <td>
               <Button
                  disabled={checkAll}
                  onClick={() => getCheckAll()}
                  className="!px-8 !h-10 !py-2"
                  theme="outline-primary">
                  <Translate id="operations:check all" />
               </Button>
            </td>
            <td>
               <div onClick={() => setOpen(!open)}>
                  <ArrowDown className="h-3 w-3 transition-all cursor-pointer" />
               </div>
            </td>
         </tr>
         <AnimatePresence>
            {open && (
               <>
                  {booking?.tackets?.map((ticket: TicketProps, index: number) => (
                     <tr key={index}>
                        <td colSpan={6} className="p-0">
                           <CheckItem booking={booking} ticket={ticket} checkAll={checkAll} />
                        </td>
                     </tr>
                  ))}
               </>
            )}
         </AnimatePresence>
      </>
   );
};

type CheckItemType = {
   checkAll: boolean;
   ticket: TicketProps;
   booking: BookingProps;
};

const CheckItem = ({ checkAll, ticket, booking }: CheckItemType) => {
   const { lang } = useTranslate();
   const [checked, setChecked] = useState(ticket.checked_in);
   useEffect(() => {
      if (checkAll) setChecked(true);
   }, [checkAll]);

   const getCheck = async () => {
      try {
         const { data } = await Axios.post(`/hy-providers/v1/bookings/${booking?._id}/check-in`, {
            ticket: ticket._id,
            check_all: false,
         });
         if (data?.status === 'Success') {
            setChecked(true);
         } else {
            throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error: any) {
         sentryCaptureException(error, {
            tags: { journey: 'business' },
         });
         errorMessage(error?.response?.data?.message);
         console.log(error);
      }
   };

   return (
      <motion.div
         key={ticket?._id}
         initial={{ height: 0 }}
         animate={{ height: 'auto' }}
         transition={{ ease: 'easeInOut' }}
         exit={{ height: 0 }}
         className="overflow-hidden">
         <div className="grid grid-cols-3 lg:grid-cols-5 border-black border-opacity-10 bg-grey-600  items-center border-b xl:text-sm text-xs  ">
            <div className="p-2 py-7">{ticket?._id}</div>
            <div>{ticket?.name[lang] ? (ticket?.name as any)?.[lang] : ticket.name}</div>
            <div></div>
            <div className="col-span-2 flex justify-center ml-16">
               <Button
                  onClick={() => getCheck()}
                  className={classNames(
                     checked && '!bg-grey-400 !ring-grey-400 !border-grey-400 !text-grey-100',
                     '!px-8 transition hover:scale-95!h-10 !py-2'
                  )}
                  disabled={checked}
                  theme={checked ? 'secondary' : 'outline-primary'}>
                  {checked ? (
                     <div className="flex items-center gap-2">
                        <TickMark className="[&>g>path]:!fill-grey-100" />
                        <span className="font-semibold">
                           <Translate id="operations:checked" />
                        </span>
                     </div>
                  ) : (
                     <Translate id="operations:check in" />
                  )}
               </Button>
            </div>
         </div>
      </motion.div>
   );
};
export default AttendeeTable;
